import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const normalizeDate = (date) => {
    if (!date) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

// Function to format date as YYYY-MM-DD
const formatDateToYYYYMMDD = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Parse YYYY-MM-DD as local calendar date (avoids UTC off-by-one in the label)
const parseYYYYMMDDToDate = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
    const [y, m, d] = parts;
    return new Date(y, m - 1, d);
};

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDisplayDate = (date) => {
    if (!date) return '';
    const d = normalizeDate(date);
    return `${d.getDate()} ${SHORT_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const DateRangePicker = ({ value, onChange, className = '', label = '', required = false, compact = false, ...props }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [localStart, setLocalStart] = useState(null);
    const [localEnd, setLocalEnd] = useState(null);

    // Update local state when opened or when props value changes while closed
    useEffect(() => {
        if (!isOpen) {
            setLocalStart(value.start ? parseYYYYMMDDToDate(value.start) : null);
            setLocalEnd(value.end ? parseYYYYMMDDToDate(value.end) : null);
        }
    }, [isOpen, value.start, value.end]);

    const formatDateRange = (start, end) => {
        if (!start && !end) return props.placeholder || 'Select date range';
        if (!start) return formatDisplayDate(end);
        if (!end) return `${formatDisplayDate(start)} → …`;
        const a = formatDateToYYYYMMDD(normalizeDate(start));
        const b = formatDateToYYYYMMDD(normalizeDate(end));
        if (a === b) return formatDisplayDate(start);
        return `${formatDisplayDate(start)} – ${formatDisplayDate(end)}`;
    };

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setLocalStart(start);
        setLocalEnd(end);

        if (start && end) {
            const formattedStart = formatDateToYYYYMMDD(normalizeDate(start));
            const formattedEnd = formatDateToYYYYMMDD(normalizeDate(end));
            onChange({ start: formattedStart, end: formattedEnd });
            setIsOpen(false);
        }
    };

    const handleClose = () => {
        // If a single date was selected but range not completed, treat as single date
        if (localStart && !localEnd) {
            const formattedStart = formatDateToYYYYMMDD(normalizeDate(localStart));
            onChange({ start: formattedStart, end: null });
        }
        setIsOpen(false);
    };

    const clearDates = (e) => {
        e.stopPropagation();
        setLocalStart(null);
        setLocalEnd(null);
        onChange({ start: null, end: null });
        setIsOpen(false);
    };

    const CalendarIcon = () => (
        <svg className={`${compact ? 'w-4 h-4 text-blue-600' : 'w-5 h-5 text-primary'} shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
    );

    return (
        <div className={`form-control w-full ${className} ${isOpen ? 'relative z-[100]' : ''}`}>
            {/* Label */}
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}

            <div className="relative">
                <div
                    className={
                        compact
                            ? 'flex items-center justify-between w-full min-w-[200px] max-w-md px-3 py-2 border border-gray-200 rounded-xl bg-gray-50/80 cursor-pointer hover:bg-white hover:border-blue-200 hover:shadow-sm focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-all duration-200'
                            : 'flex items-center justify-between w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white cursor-pointer hover:border-gray-400 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/50 transition-all duration-200'
                    }
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <div className="flex items-center gap-2 min-w-0">
                        <CalendarIcon />
                        <span className={`truncate ${compact ? 'text-xs font-medium' : 'text-sm'} ${localStart || localEnd ? 'text-gray-800' : 'text-gray-400'}`}>
                            {formatDateRange(localStart, localEnd)}
                        </span>
                    </div>

                    {(localStart || localEnd) && (
                        <button
                            onClick={clearDates}
                            className={`text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full flex items-center justify-center transition-colors duration-200 shrink-0 ${compact ? 'text-base w-5 h-5 ml-1' : 'text-lg ml-2 w-6 h-6'}`}
                            type="button"
                        >
                            ×
                        </button>
                    )}
                </div>

                {isOpen && (
                    <>
                        <div
                            className="fixed inset-0 z-40"
                            onClick={handleClose}
                        />

                        <div className="absolute top-full left-0 right-0 sm:right-auto mt-1.5 z-[200] rounded-xl border border-gray-200 bg-white shadow-xl overflow-hidden sm:min-w-[280px]">
                            <DatePicker
                                onChange={handleDateChange}
                                startDate={localStart}
                                endDate={localEnd}
                                selectsRange
                                inline
                                monthsShown={1}
                                calendarClassName="!border-0 !shadow-none"
                                {...props}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default DateRangePicker;